import type { ComponentProps, ReactNode } from "react";
import { forwardRef } from "react";
import { Link } from "react-router-dom";

import { cn } from "@/lib/cn";

type Variant = "primary" | "secondary" | "ghost" | "link";
type Size = "sm" | "md" | "lg";

const base =
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50";

// Pill shapes across the board — matches the liquid-glass navbar chrome.
const variants: Record<Variant, string> = {
  primary:
    "rounded-full bg-primary text-primary-foreground hover:bg-primary-press hover:shadow-glow-accent hover:scale-[1.02] active:scale-[0.98]",
  secondary:
    "rounded-full border border-input bg-card text-foreground hover:bg-muted hover:scale-[1.02] active:scale-[0.98]",
  ghost: "rounded-full text-muted-foreground hover:bg-muted hover:text-foreground",
  link: "text-primary hover:text-primary-press underline-offset-4 hover:underline",
};

const sizes: Record<Size, string> = {
  sm: "h-9 px-3.5 text-sm",
  md: "h-11 px-5 text-sm",
  lg: "h-12 px-6 text-base",
};

type CommonProps = {
  variant?: Variant;
  size?: Size;
  className?: string;
  leading?: ReactNode;
  trailing?: ReactNode;
  children: ReactNode;
};

type ButtonAsButton = CommonProps & ComponentProps<"button"> & { to?: undefined; href?: undefined };
type ButtonAsLink = CommonProps & { to: string } & Omit<
    ComponentProps<typeof Link>,
    "to" | "children"
  >;
type ButtonAsAnchor = CommonProps & { href: string } & Omit<
    ComponentProps<"a">,
    "href" | "children"
  >;

export type ButtonProps = ButtonAsButton | ButtonAsLink | ButtonAsAnchor;

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  function Button(props, ref) {
    const {
      variant = "primary",
      size = "md",
      className,
      leading,
      trailing,
      children,
      ...rest
    } = props;
    const classes = cn(base, variants[variant], sizes[size], className);
    const content = (
      <>
        {leading ? <span className="inline-flex shrink-0">{leading}</span> : null}
        <span>{children}</span>
        {trailing ? <span className="inline-flex shrink-0">{trailing}</span> : null}
      </>
    );

    if ("to" in rest && rest.to !== undefined) {
      const { to, ...linkProps } = rest;
      return (
        <Link to={to} ref={ref as React.Ref<HTMLAnchorElement>} className={classes} {...linkProps}>
          {content}
        </Link>
      );
    }

    if ("href" in rest && rest.href !== undefined) {
      const { href, ...anchorProps } = rest;
      return (
        <a
          href={href}
          ref={ref as React.Ref<HTMLAnchorElement>}
          className={classes}
          {...anchorProps}
        >
          {content}
        </a>
      );
    }

    const buttonProps = rest as ComponentProps<"button">;
    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type={buttonProps.type ?? "button"}
        className={classes}
        {...buttonProps}
      >
        {content}
      </button>
    );
  },
);
